import React, { useState, useEffect, useRef } from 'react';
import './Characters.css';

// Import images
import hotshotPortrait from '../../../assets/images/characters/hotshot300x800.png';
import hotshotFullBody from '../../../assets/images/characters/hotshot-headshot.png';
import keeperPortrait from '../../../assets/images/characters/keeper300x800.png';
import keeperFullBody from '../../../assets/images/characters/keeper-headshot.png';
import fluidPortrait from '../../../assets/images/characters/fluid300x800.png';
import fluidFullBody from '../../../assets/images/characters/fluid-headshot.png';
import portraitBackground from '../../../assets/images/characters/background.png';

const Characters = () => {
  const [activeCharacter, setActiveCharacter] = useState('hotshot');
  const [characterBubbles, setCharacterBubbles] = useState({});
  const [activePosition, setActivePosition] = useState('hotshot');
  const [currentBpm, setCurrentBpm] = useState(80);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isChangingCharacter, setIsChangingCharacter] = useState(false);

  const characters = [
    {
      id: 'hotshot',
      name: 'Hotshot',
      description: 'Aggressive and starved for stimulation, seeking relevance in a world that has passed humanity by. She has the drive of an overachiever, a laser focus, with one objective: to relentlessly cut down any challenge set before her.',
      playstyle: 'Hard and embodies a hotblooded youth with boundless energy. Deals damage with reckless abandon and gains power when playing aggressively.',
      drones: {
        swarm: 'Overwhelms foes with fire power and superior numbers.',
        observer: 'Revs into action, presenting the perfect outlet to do what she loves, again and again.',
        recycler: 'Mocks foes by using their bodies to construct powerful trophy weapons to tear apart their allies.'
      },
      realName: 'Tracey Playfair',
      images: {
        portrait: hotshotPortrait,
        fullBody: hotshotFullBody
      }
    },
    {
      id: 'keeper',
      name: 'Keeper',
      description: "Someone who's been through the wringer, and knows more than he lets on. He's seen people come and go, teaching them to survive within the bunker. He seeks solace in building towards humanity's better future.",
      playstyle: 'Slow and steady, laying defensive groundwork that builds up over the fight until becoming an immovable force. Can hold cards between turns.',
      drones: {
        swarm: 'Builds up protective ranks, pooling energy for heavy blows.',
        observer: 'Remembers everything, waiting for the perfect moment to strike.',
        recycler: 'Lovingly rebuilds defeated drones to serve as faithful minions.'
      },
      realName: 'Orwell Smith',
      images: {
        portrait: keeperPortrait,
        fullBody: keeperFullBody
      }
    },
    {
      id: 'fluid',
      name: 'Fluid',
      description: 'Wears an unwavering mask, polite and attentive. Every word and action is carefully crafted to please their superiors, fulfilling quotas almost perfectly.',
      playstyle: 'Breaks conventions for advanced players. Recombines cards mid-fight for a highly adaptable approach.',
      drones: {
        swarm: 'Creates a relay of signals to outmaneuver and outplay foes.',
        observer: 'Holds onto highs and lows, assuming different stances through playback analysis.',
        recycler: 'Turns fallen enemies into broadcasting totems that alter drone behavior.'
      },
      realName: 'Ciel Falling',
      images: {
        portrait: fluidPortrait,
        fullBody: fluidFullBody
      }
    }
  ];

  const toggleCharacter = (id) => {
    setActiveCharacter(activeCharacter === id ? null : id);
  };

  const generateBubblesForCharacter = () => {
    const bubbleSizes = ['small', 'medium', 'large'];
    const colors = [
      'rgba(147, 112, 219, 0.3)', // purple
      'rgba(70, 130, 180, 0.3)',  // blue
      'rgba(255, 140, 0, 0.3)'    // orange
    ];
    
    return Array.from({ length: 10 }, () => ({
      size: bubbleSizes[Math.floor(Math.random() * bubbleSizes.length)],
      bottom: 0,
      left: Math.random() * 100,
      duration: (3 + Math.random() * 5).toFixed(2),
      delay: (Math.random() * 2).toFixed(2),
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
  };

  useEffect(() => {
    const bubbles = {};
    characters.forEach(character => {
      bubbles[character.id] = generateBubblesForCharacter();
    });
    setCharacterBubbles(bubbles);
  }, []);

  useEffect(() => {
    const updateBpm = () => {
      setCurrentBpm(prevBpm => {
        const min = Math.max(60, prevBpm - 5);
        const max = Math.min(100, prevBpm + 5);
        return Math.floor(Math.random() * (max - min + 1)) + min;
      });
    };
    const interval = setInterval(updateBpm, 2000);
    updateBpm();
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getPosition = (characterId) => {
    if (characterId === activeCharacter) return 'front';
    const positions = ['hotshot', 'keeper', 'fluid'];
    const activeIndex = positions.indexOf(activeCharacter);
    const currentIndex = positions.indexOf(characterId);
    
    if ((currentIndex === activeIndex + 1) || (activeIndex === 2 && currentIndex === 0)) {
      return 'right';
    }
    return 'left';
  };

  const rotateCarousel = (id) => {
    setActivePosition(id);
  };

  const navigateCharacter = (direction) => {
    const positions = ['hotshot', 'keeper', 'fluid'];
    const currentIndex = positions.indexOf(activeCharacter);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % positions.length;
    } else {
      newIndex = (currentIndex - 1 + positions.length) % positions.length;
    }

    if (windowWidth <= 768) {
      setIsChangingCharacter(true);
      setTimeout(() => {
        setActiveCharacter(positions[newIndex]);
        setTimeout(() => {
          setIsChangingCharacter(false);
        }, 50);
      }, 300);
    } else {
      setActiveCharacter(positions[newIndex]);
    }
  };

  const titleContainerRef = useRef(null);
  const squareRef = useRef(null);
  const characterContainerRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && titleContainerRef.current) {
        titleContainerRef.current.classList.add('active');
      }
    }, { threshold: 0.99 });
    if (titleContainerRef.current) observer.observe(titleContainerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const characterObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        characterContainerRef.current.classList.add('fade-in');
      }
    }, { threshold: 0.2 });

    if (characterContainerRef.current) characterObserver.observe(characterContainerRef.current);
    return () => characterObserver.disconnect();
  }, []);

  const shortenText = (text) => {
    const maxLength = window.innerWidth <= 576 ? 60 : window.innerWidth <= 990 ? 80 : 100;
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="page-container">
      <div className="characters-title-container" ref={titleContainerRef}>
        <div className="title-container-background"></div>
        <h2 className="fns-title-font-character">The Awakened Few.<span className="cursor">|</span></h2>
        <div className="digital-lines-game"></div>
      </div>
      <div className="characters-container" ref={characterContainerRef}>

        
        {/* New background div */}
        <div className="carousel-background"></div>
        
        <div className="carousel-wrapper">
          {/* Add navigation buttons */}
          <button 
            className="character-nav-button prev-button" 
            onClick={() => navigateCharacter('prev')}
            aria-label="Previous character"
          >
            &#10094;
          </button>
          
          {characters.map((character) => (
            <React.Fragment key={character.id}>
              <div 
                className={`character-card ${isChangingCharacter ? 'changing' : ''}`}
                data-position={getPosition(character.id)}
                onClick={() => setActiveCharacter(character.id)}
              >
                <div className="character-image-container" style={{ backgroundImage: `url(${portraitBackground})` }}>
                  <img 
                    src={character.images.portrait} 
                    alt={character.name} 
                    className="character-image"
                  />
                  <div className="overlay" />
                  {characterBubbles[character.id]?.map((bubble, index) => (
                    <div
                      key={index}
                      className={`bubble ${bubble.size}`}
                      style={{
                        bottom: `${bubble.bottom}%`,
                        left: `${bubble.left}%`,
                        backgroundColor: bubble.color,
                        animationName: 'rise',
                        animationDuration: `${bubble.duration}s`,
                        animationDelay: `${bubble.delay}s`,
                        animationTimingFunction: 'ease-in',
                        animationIterationCount: 'infinite'
                      }}
                    ></div>
                  ))}
                </div>
              </div>
              
              <div className={`character-info-panel ${isChangingCharacter ? 'changing' : ''}`}>
                <div className="character-info-effects">
                  <img 
                    src={character.images.fullBody} 
                    alt={`${character.name} headshot`} 
                    className="character-headshot"
                  />
                  <div className="eq-visualizer">
                    {[...Array(windowWidth <= 576 ? 8 : windowWidth <= 990 ? 12 : 15)].map((_, i) => (
                      <div key={i} className="eq-bar" style={{
                        animationDelay: `${i * 0.1}s`,
                        animationDuration: `${0.5 + Math.random() * 1}s`
                      }}></div>
                    ))}
                  </div>
                  <div className="bpm-meter">
                    <div className="bpm-pulse"></div>
                  </div>
                  <div className="bpm-value">{currentBpm}<div style={{fontSize:"0.7rem", display:'inline'}}>bpm</div></div>
                  <span
                    key={`${character.id}-${activeCharacter}`}
                    className="typing-effect"
                  >
                    {character.realName}
                  </span>
                </div>
                <div className='character-info'>
                  <h2>{character.name}</h2>
                  <p>{character.description}</p>
                  <h3>Playstyle</h3>
                  <p>{character.playstyle}</p>
                  <h3>Drone Abilities</h3>
                  <ul>
                    <li>{shortenText(character.drones.swarm)}</li>
                    <li>{shortenText(character.drones.observer)}</li>
                    <li>{shortenText(character.drones.recycler)}</li>
                  </ul>
                </div>
              </div>
            </React.Fragment>
          ))}
          
          <button 
            className="character-nav-button next-button" 
            onClick={() => navigateCharacter('next')}
            aria-label="Next character"
          >
            &#10095;
          </button>
        </div>
      </div>
    </div>
  );
};

export default Characters;